import classNames from 'classnames';
import type { Stage as KonvaStage } from 'konva/lib/Stage';
import React, { useMemo, useRef, useState, useCallback } from 'react';

import { KonvaImagePromisesProvider } from 'common/components/RewindModal/Konvas/KonvaImagePromisesProvider';
import ShareableScreenPodium from 'common/components/RewindModal/Konvas/ShareableScreenPodium';
import Slides from 'common/components/RewindModal/Slides';

import styles from '../styles.module.scss';
import BottomActions from './component/BottomActions';
import Card from './component/Card';

type TopContent = {
  id: string;
  position: number;
  rating: number;
  title: string;
  poster?: { path: string };
};

type ScreenTop10ContentProps = {
  data: any;
  entities: any;
  onNext: () => void;
  isLastStep: boolean;
  screenName: ScreenNames;
  allScreens: any[];
  trackingScreenName?: string;
};

const ScreenTop10ContentInner = ({
  data,
  entities,
  onNext,
  isLastStep,
  screenName,
  allScreens,
  isDownloadReady
}: ScreenTop10ContentProps & { isDownloadReady: boolean }) => {
  const stageRef = useRef<KonvaStage>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const isSeries = useMemo(() => screenName.includes('series'), [screenName]);

  const topContent = useMemo((): TopContent[] => {
    const rankedContent = isSeries ? data?.series : data?.movies;
    const contentEntities = entities?.[0]?.entities ?? [];
    if (!rankedContent) return [];
    return rankedContent
      .map((item: any, index: number) => {
        const itemData = contentEntities.find(
          (entity: any) => entity.id === item.gid_entity
        );
        return { ...itemData, position: index + 1, rating: item.rating };
      })
      .filter(Boolean);
  }, [data, entities, isSeries]);

  const loopedContent = useMemo(
    () => [...topContent, ...topContent],
    [topContent]
  );

  const flashbackType = useMemo(() => {
    const flashbackScreen = allScreens.find(s => s.name === 'flashback_type');
    return flashbackScreen?.data?.flashback_type;
  }, [allScreens]);

  const { title, subtitle, shareText } = useMemo(() => {
    const isGlobal = flashbackType === 6;
    if (isSeries) {
      return {
        title: 'Top séries',
        subtitle: isGlobal
          ? 'Elles ont duré des saisons… ou juste un soir. En 2025, elles ont marqué tous les écrans.'
          : 'Elles ont duré des saisons… ou juste un soir. En 2025, elles ont marqué votre écran.',
        shareText: 'Partager mon top séries'
      };
    } else {
      return {
        title: isGlobal ? 'Top Films' : 'Top films',
        subtitle: isGlobal
          ? "Pas besoin de jury : l'académie des cinéphiles AlloCiné a parlé"
          : "Ils viennent d'hier, d'avant-hier ou de très loin.. et vous les avez validés en 2025",
        shareText: 'Partager mon top films'
      };
    }
  }, [isSeries, flashbackType]);

  const podiumItems = useMemo(() => {
    const validItems = topContent
      .slice(0, 3)
      .filter((item: any) => item && item.id);
    return validItems.map((item: any, index: number) => ({
      id: item.id,
      position: index + 1,
      poster: item.poster
    }));
  }, [topContent]);

  const handleDownload = useCallback(async () => {
    if (!isDownloadReady || isDownloading || !stageRef.current) {
      return;
    }

    setIsDownloading(true);

    setTimeout(() => {
      try {
        const stage = stageRef.current;
        if (!stage) {
          throw new Error('Konva stage is not available.');
        }

        const dataURL = stage.toDataURL({ pixelRatio: 2 });

        const link = document.createElement('a');
        link.download = `mon-top-allocine-2025.png`;
        link.href = dataURL;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error generating image:', error);
      } finally {
        setIsDownloading(false);
      }
    }, 100);
  }, [isDownloadReady, isDownloading]);

  return (
    <>
      <Slides>
        <div
          className={classNames(styles.contentLayout, styles.centeredLayout)}
        >
          <div className={classNames(styles.centerTextFixed, styles.top10Text)}>
            <h2 className={styles.slideTitle}>{title}</h2>
            <h3 className={styles.slideText}>{subtitle}</h3>
          </div>
          <div className={styles.top10ListWrapper}>
            <div className={styles.top10List}>
              {loopedContent.map((item, index) => (
                <div
                  key={`${item.id}-${index}`}
                  className={classNames(
                    styles.topCardItem,
                    `top10-card-${item.position}`
                  )}
                >
                  <Card path={item.poster?.path} />
                  <div className={styles.topCardRank}>
                    <span className={styles.topCardRankNum}>
                      {item.position}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <BottomActions
          showShare={true}
          onShareClick={handleDownload}
          isShareDisabled={!isDownloadReady || isDownloading}
          shareText={isDownloading ? 'Génération...' : shareText}
          showNext={!isLastStep}
          onNextClick={onNext}
        />
      </Slides>

      <ShareableScreenPodium
        ref={stageRef}
        data={{ title }}
        items={podiumItems}
        isSeries={isSeries}
      />
    </>
  );
};

const ScreenTop10Content = (props: ScreenTop10ContentProps) => {
  const [isDownloadReady, setIsDownloadReady] = useState(false);

  return (
    <KonvaImagePromisesProvider onDownloadableChange={setIsDownloadReady}>
      <ScreenTop10ContentInner {...props} isDownloadReady={isDownloadReady} />
    </KonvaImagePromisesProvider>
  );
};

export default ScreenTop10Content;
